/**
 * The virtual canvas. The sim works only in these units; the renderer applies
 * one scale transform. Never derive gameplay radii from device pixels or from
 * screen height — see spec §4.0.1.
 */
export const VIEW_W = 720;
export const VIEW_H = 1280;

export const CENTER_X = VIEW_W / 2; // 360
export const CENTER_Y = VIEW_H / 2; // 640

/** Planet crust. Surface slots sit here. */
export const R_PLANET = 110;
/** The orbit ring. Turns continuously; the player doesn't steer it. */
export const R_ORBIT = 210;
/**
 * Default radius for artificial stations — outside the orbit, inside the
 * spawn circle, so a station meets an attack run before the ring does. Levels
 * may override it per station.
 */
export const R_STATION = 258;
/** Enemies begin their attack run from this circle, not the screen edge (§4.0). */
export const R_SPAWN = 300;
/** Where the staging flight fans out from — inside the corridor, off-playfield. */
export const R_CORRIDOR = 430;
/** Off-screen entry radius. */
export const R_ENTRY = 780;

/** Fixed simulation timestep. */
export const TICK_HZ = 60;
export const DT = 1 / TICK_HZ;

/** Three-beat entrance (§6.1). */
export const ENTER_DUR = 0.85;
export const HOLD_DUR = 1.0;

/**
 * The ring is in orbit and nobody steers it — it laps the planet about every
 * fourteen seconds on its own.
 *
 * That makes the two layers genuinely different jobs. The crust *holds* a
 * bearing: mounts there cover the arc you put them on. The ring *sweeps*: every
 * bearing gets covered eventually, nothing is covered permanently, and juice
 * anywhere on its band gets harvested without the player doing anything.
 *
 * The cost of this, stated plainly: there is no longer a "spin to meet the
 * threat" decision. Ring coverage is a rhythm you build around rather than a
 * move you make.
 */
export const RING_ORBIT_RATE = 0.45;

/** Ring feel. Tuned in the prototype; risk #1 lives here. */
/**
 * Mass on the ring slows its orbit. Physically that's wrong — an orbital period
 * doesn't care about the satellite's mass — but it's the only thing left that
 * makes loading the ring with moons a decision rather than a free upgrade, and
 * it keeps the spirit of §4.3: a heavy ring covers less ground.
 */
export const RING_MASS_DRAG = 0.55;

/** Surface slots ride the planet's natural spin unless locked. */
export const SURFACE_LOCK_COST = 2;

/** Economy. */
export const SELL_REFUND = 0.7;
export const WAVE_CLEAR_BONUS = 4;
export const CORE_HP_DEFAULT = 100;

/* --- Juice drops ----------------------------------------------------- */

/** How long a dropped juice sits there before fading. Deliberately generous. */
export const DROP_LIFETIME = 9;
/** Tap radius. Well over the 44-unit minimum target size (§9). */
export const DROP_TAP_RADIUS = 46;
/** The orbit ring sweeps up anything within this distance of it. */
export const DROP_RING_BAND = 30;
/** Juice drift outward a little so they don't die under the crust. */
export const DROP_DRIFT = 26;
/** Pickup animation length, purely cosmetic. */
export const DROP_COLLECT_TIME = 0.35;

/** Entity caps — legibility beats spectacle inside a 420-unit circle (risk #6). */
export const MAX_ENEMIES = 90;
export const MAX_PROJECTILES = 300;
export const MAX_EFFECTS = 48;
export const MAX_DROPS = 120;

export const TAU = Math.PI * 2;
